import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Controller, FieldValues, FormProvider, useForm } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import moment from "moment";
import uniqBy from "lodash.uniqby";

import { useLazyQuery, useMutation } from "@apollo/client";

import { Button, Card, Checkbox, FormGroup, Input, PageHeader, Select, showSnackbar } from "@cofano/cck";
import { Alert, Box, Tab, Tabs } from "@mui/material";

import FormField from "components/cck/FormField";
import Helmet from "components/helmet/Helmet";
import ContractStatusSelect from "components/contract/ContractStatusSelect";
import LabelSelect from "view/labels/components/selects/LabelSelect";
import CurrencySelect from "components/selects/CurrencySelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import { Fields, hasOptionalFieldPermission } from "components/utils/Permission";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";
import NoticePeriodSelect from "components/selects/NoticePeriodSelect";
import { useConfirm } from "components/hooks/useConfirm";
import DateRangePicker from "components/cck/DateRangePicker";
import useTabs from "components/hooks/useTabs";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import ProductCategorySelect from "view/productcategories/components/selects/ProductCategorySelect";
import ContractOptionsForm from "view/contractsNew/components/form/ContractOptionsForm";

import {
	AgreementTypeEnum,
	CalculationMethodType,
	CharterType,
	CharterSubtype,
	ContractStatus,
	LabelType,
	LocationType,
	RelationType,
} from "__generated__/enums";

import { Language } from "imports/Utils";

import {
	BACK_TO_BACK_CONTRACT_MUTATION,
	CHARTER_QUERY,
	CONTRACT_FORM_QUERY,
	CONTRACT_MUTATION,
} from "./utils/ContractQueries";

import {
	CargoLinkedCharter,
	PurchaseCharter,
	SalesCharter,
} from "types/index";
import { Contact, CreateEditEntityInDialogProps } from "types/commons";
import { DossierLink } from "utils/LinkUtils";
import "./styling/styling.scss";
import AgreementCreateEdit from "view/agreements/AgreementCreateEdit";
import Grid from "@mui/material/Grid2";
import { zodResolver } from "@hookform/resolvers/zod";
import { parentContractCompatibleSubtypes } from "view/contractsNew/utils/ContractUtils";
import {
	ContractFormInput,
	ContractFormOutput,
	ContractValidationSchema,
} from "view/contractsNew/validation/contract.zod";
import { getAgreements_query } from "view/agreements/utils/AgreementQueries";
import { convertAgreementToForm } from "view/agreements/utils/AgreementUtils";
import TermsAndConditionsSelect from "view/terms/components/selects/TermsAndConditionsSelect";
import { PurchaseCharterFromQuery } from "view/charters/PurchaseCharterDetails";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import TrackedEntityForm from "components/entityHistory/components/form/TrackedEntityForm";
import { PluginFields } from "types/enums";

const TABS = {
	AGREEMENTS: "agreements",
	OPTIONS: "options",
};

const ContractCreateEdit = ({
	linkedCharter,
	pageInDialogSettings,
	hideContractDetails = false,
	createSalesContract = false,
}: {
	linkedCharter?: CargoLinkedCharter | PurchaseCharterFromQuery;
	pageInDialogSettings?: CreateEditEntityInDialogProps;
	hideContractDetails?: boolean;
	createSalesContract?: boolean;
}) => {

	const params = useParams();
	const id = pageInDialogSettings ? pageInDialogSettings.entityId : params.id;

	const navigate = useNavigate();
	const confirm = useConfirm();
	const { state } = useLocation();

	const session = useStoreState((store) => store.session);

	const charterLinkingMessage = intl.formatMessage({ id: "THE_CHARTER_|_WILL_BE_LINKED_TO_CONTRACT" }).split("|");

	const [ loading, setLoading ] = useState(false);
	const [ charter, setCharter ] = useState<SalesCharter | PurchaseCharter | undefined>();

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: TABS.AGREEMENTS,
		disableParams: !!pageInDialogSettings,
	});

	const defaultCurrency = {
		isoCode: "EUR",
		symbol: "€",
	};

	const defaultParentContracts = hasOptionalFieldPermission(session, PluginFields.contract, "parentContract");

	const form = useForm<ContractFormInput, unknown, ContractFormOutput>({
		resolver: zodResolver(ContractValidationSchema),
		mode: "onSubmit",
		reValidateMode: "onChange",
		defaultValues: {
			contractNumber: "",
			status: null,
			charterType: CharterType.Sales,
			charterSubtype: CharterSubtype.Contract,
			description: "",
			memo: "",
			labels: [],
			currency: defaultCurrency,
			creditor: null,
			creditorContact: undefined,
			debtor: null,
			debtorContact: undefined,
			broker: null,
			validWindow: [ null, null ],
			noticePeriod: undefined,
			versionNumber: "",
			allIn: false,
			agreements: [],
			regions: [],
			productCategory: undefined,
			charters: [],
			isParentContract: defaultParentContracts ?? false,
			parentContract: null,
			contractOptions: [],
			takenOptionContract: null,
			isBackToBack: false,
			includePricingInNomination: true,
			termsAndConditions: undefined,
		} satisfies ContractFormInput,
	});

	const { watch, reset, control, formState: { errors }, handleSubmit, setValue, setError } = form;

	const isParentContract = watch("isParentContract");
	const parentContract = watch("parentContract");
	const charterType = watch("charterType");
	const charterSubtype = watch("charterSubtype");
	const agreements = watch("agreements");
	const creditor = watch("creditor");
	const debtor = watch("debtor");
	const charters = watch("charters");
	const currency = watch("currency");
	const validWindow = watch("validWindow");
	const contractOptions = watch("contractOptions");
	const takenOptionContract = watch("takenOptionContract");

	const isChildContract = id != null && isParentContract === false && parentContract != null;
	const validityWindowRequired = charterSubtype !== CharterSubtype.Spot;
	const canBeBackToBack = id == null && isParentContract && charterType === CharterType.Sales && charterSubtype === CharterSubtype.Time;

	const isTimePurchaseContract = charterType === CharterType.Purchase && charterSubtype === CharterSubtype.Time;

	const [ getContract ] = useLazyQuery(CONTRACT_FORM_QUERY);
	const [ getAgreements ] = useLazyQuery(getAgreements_query);
	const [ getCharter ] = useLazyQuery(CHARTER_QUERY);

	const [ saveContract ] = useMutation(CONTRACT_MUTATION);
	const [ saveBackToBackContract ] = useMutation(BACK_TO_BACK_CONTRACT_MUTATION);

	const handleSave = async (data: ContractFormOutput, askForRemark: () => Promise<string | null>) => {
		if (data.id && !await checkValidity(data)) {
			return;
		}
		try {
			save(data, await askForRemark());
		} catch {
			// Remark dialog has been closed
		}
	};

	const checkValidity = async (data: ContractFormOutput) => {

		const charter = data.charters?.[0] || undefined;
		const charterValidFrom = charter?.validFrom ? moment(charter.validFrom).unix() * 1000 : undefined;
		const charterValidUntil = charter?.validUntil ? moment(charter.validUntil).unix() * 1000 : undefined;

		if (data.charterType === CharterType.Purchase && data.charterSubtype === CharterSubtype.Time && charter) {
			if (((data.validFrom !== charterValidFrom) || (data.validUntil !== charterValidUntil)) && data.id) {
				try {
					await confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "UPDATE_LINKED_CHARTER_VALIDITY" }),
					});
				} catch {
					return false;
				}
			}
		}
		return true;
	};

	const save = (data: ContractFormOutput, remark: string | null) => {
		if (data.isBackToBack && data.charterType === CharterType.Sales && data.charterSubtype === CharterSubtype.Time) {
			return saveBackToBack(data, remark);
		}

		setLoading(true);

		saveContract({
			variables: {
				input: {
					...data,
					charters: charter ? [ { id: charter.id } ] : [],
				},
				createMatchingContracts: createSalesContract,
				remark: remark,
			},
		}).then((response) => {
			if (pageInDialogSettings?.onSuccess) {
				pageInDialogSettings.onSuccess();
			} else {
				navigate(`/view/contracts/${response.data?.saveContract?.id}/details`);
			}

		}).finally(() => {
			setLoading(false);
		});
	};

	const saveBackToBack = (data: ContractFormOutput, remark: string | null) => {
		setLoading(true);

		saveBackToBackContract({
			variables: {
				input: data,
				remark: remark,
			},
		}).then((response) => {
			if (pageInDialogSettings?.onSuccess) {
				pageInDialogSettings.onSuccess();
			} else {
				showSnackbar({
					message: `${intl.formatMessage({ id: "BACK_TO_BACK_SAVED_AS" })} ${response.data?.saveBackToBackContract?.purchase?.contractNumber}`,
					variant: "info",
				});
				navigate(`/view/contracts/${response.data?.saveBackToBackContract?.sales?.id}/details`);
			}
		}).finally(() => {
			setLoading(false);
		});
	};

	const getCreditorType = () => {
		if (charterSubtype === CharterSubtype.Supplier) {
			return [ RelationType.Supplier, RelationType.ApplicationRelation ];
		} else if (charterType === CharterType.Sales) {
			return [ RelationType.ApplicationRelation ];
		} else if (charterType === CharterType.Purchase) {
			return [ RelationType.Operator, RelationType.ShipManager, RelationType.Skipper ];
		} else {
			return [];
		}
	};

	const getDebtorType = () => {
		if (charterSubtype === CharterSubtype.Supplier) {
			return [ RelationType.Supplier, RelationType.ApplicationRelation ];
		} else if (charterType === CharterType.Sales) {
			return [ RelationType.Customer ];
		} else if (charterType === CharterType.Purchase) {
			return [ RelationType.ApplicationRelation ];
		} else {
			return [];
		}
	};

	const isCharterTypeFieldDisabled = (field: FieldValues) => {
		if (id || field.disabled || agreements.length > 0 || isChildContract) {
			return true;
		} else {
			return !!charter;
		}
	};

	useEffect(() => {
		if (id) {

			setLoading(true);

			getContract({
				variables: {
					id: Number(id),
				},
			}).then(response => {

				const contract = response.data?.contract;
				const validFrom = contract?.validFrom || null;
				const validUntil = contract?.validUntil || null;

				if (contract) {
					reset({
						...contract,
						contractNumber: contract.contractNumber ?? "",
						description: contract.description ?? "",
						validWindow: [ validFrom ? new Date(validFrom) : null, validUntil ? new Date(validUntil) : null ],
						charterType: contract.charterType,
						charterSubtype: contract.charterSubtype,
						contractOptions: (contract.charterType === CharterType.Purchase && contract.charterSubtype === CharterSubtype.Time) ? contract.contractOptions?.map(option => ({
							...option,
							id: option.id,
							description: option.description ?? "",
							indexationDate: option.indexationDate ?? null,
							indexationAmount: option.indexationAmount ?? null,
							noticePeriod: option.noticePeriod ?? undefined,
							validityWindow: {
								from: option.validFrom,
								to: option.validUntil,
							},
							optionTaken: option.optionTaken ?? null,
							pricePerUnit: option.pricePerUnit ?? null,
							validFrom: undefined,
							validUntil: undefined,
						})) ?? [] : [],
						labels: contract.labels ?? [],
						isParentContract: contract.isParentContract ?? false,
						parentContract: contract.parentContract ? {
							id: contract.parentContract.id,
							contractNumber: contract.parentContract.contractNumber ?? "",
						} : null,
						regions: contract.regions ?? [],
						agreements: [],
						creditor: contract.creditor ?? null,
						debtor: contract.debtor ?? null,
						takenOptionContract: contract.takenOptionContract ? {
							id: contract.takenOptionContract.id,
							contractNumber: contract.takenOptionContract.contractNumber ?? "",
						} : null,
						memo: contract.memo ?? "",
						versionNumber: contract.versionNumber ?? "",
						allIn: contract.allIn ?? false,
					} satisfies ContractFormInput);

					getAgreements({
						variables: {
							search: {
								contractId: contract?.id,
							},
						},
					}).then(({ data }) => {
						const agreements = data?.agreements
							?.map(x => convertAgreementToForm(x)) ?? [];

						setValue("agreements", agreements);
					});
				}

				setLoading(false);
			});

		} else if (linkedCharter || state?.charterId) {

			getCharter({
				variables: {
					id: Number(linkedCharter?.id || state?.charterId),
				},
			}).then(response => {

				if (response.data?.charter) {

					const currentCharter = response.data.charter as (SalesCharter | PurchaseCharter);
					const creditor = currentCharter.creditor;
					const debtor = currentCharter.debtor;

					setCharter(currentCharter);

					setValue("charterType", currentCharter.type);
					// If the charter already has a contract, it is only possible to add supplier contracts to it
					setValue("charterSubtype", linkedCharter?.contract ? CharterSubtype.Supplier : currentCharter.subtype);
					setValue("creditor", {
						id: creditor.id,
						name: creditor.name,
						contacts: creditor.contacts as Contact[],
					});
					setValue("debtor", { id: debtor.id, name: debtor.name, contacts: debtor.contacts as Contact[] });

					if (currentCharter.subtype !== CharterSubtype.Spot) {
						setValue("validWindow", [ currentCharter.validFrom ? moment(currentCharter.validFrom).toDate() : null, currentCharter.validUntil ? moment(currentCharter.validUntil).toDate() : null ]);
					}

					if (currentCharter.voyages && currentCharter.voyages.length > 0 && !createSalesContract) {
						setValue("agreements", [ {
							id: undefined,
							description: "",
							type: AgreementTypeEnum.Ship,
							ships: uniqBy(currentCharter.voyages.map(v => ({ id: v.ship.id, name: v.ship.name })), "id"),
							calculationMethod: {
								type: CalculationMethodType.Manual,
							},
						} ]);
					}
				}
			});
		}
	}, []);

	useEffect(() => {
		if (pageInDialogSettings?.onLoadingChange) {
			pageInDialogSettings.onLoadingChange(loading);
		}
	}, [ loading ]);

	return (
		<div className="contract-create-edit">
			<FormProvider {...form}>
				<TrackedEntityForm
					id="contractCreateEditForm"
					onSubmit={(askForRemark) => handleSubmit(async (data) => {
						await handleSave(data, askForRemark);
					})}
				>
					{!pageInDialogSettings && (
						<PageHeader>
							<PageHeader.Title>
								{Language("CONTRACT")}
								<Helmet title={intl.formatMessage({ id: "CONTRACT" })} />
							</PageHeader.Title>

							<PageHeader.Actions>
								<Button disabled={loading}
									onClick={() => navigate(id ? `/view/contracts/${id}/details` : "/view/contracts")}
									variant="outlined">
									{Language("CANCEL")}
								</Button>

								<Button disabled={loading} loading={loading} type="submit">
									{Language("SAVE")}
								</Button>
							</PageHeader.Actions>
						</PageHeader>
					)}

					{charter && !id && (
						<Alert severity="warning" style={{ marginBottom: 16 }}>
							<span>{charterLinkingMessage[0]}</span>
							<span><DossierLink charter={charter} /></span>
							<span>{charterLinkingMessage[1]}</span>
						</Alert>
					)}

					{createSalesContract && (
						<Alert severity="warning" style={{ marginBottom: 16 }}>
							<span>{intl.formatMessage({ id: "SALES_CONTRACT_WILL_ALSO_BE_CREATED" })}</span>
						</Alert>
					)}

					<Grid spacing={3} container>
						{!hideContractDetails && (
							<Grid size={{ md: 4, xs: 12 }}>
								<Card>
									<FormGroup
										label={Language("CONTRACT_NUMBER")}
										labelPlacement="left-center"
										overflow="hidden">
										<Controller
											name="contractNumber"
											control={control}
											render={({ field }) => (
												<Input
													{...field}
													disabled={isChildContract}
													placeholder={intl.formatMessage({ id: "GENERATED_FIELD_PLACEHOLDER" })}
													autoFocus />
											)} />
									</FormGroup>

									<FormGroup
										label={Language("STATUS")}
										labelPlacement="left"
										labelPaddingTop={11}
										overflow="visible"
										required>
										<FormField errors={errors} name="status">
											<Controller
												name="status"
												control={control}
												rules={{ required: true }}
												render={({ field }) => {
													return (
														<React.Fragment>
															<ContractStatusSelect
																{...field}
																onInitLoad={options => {
																	const draftStatus = options.find(option => option.name === ContractStatus.Draft);
																	field.onChange(draftStatus);
																}}
																disabled={isChildContract}
															/>

															{field.value?.name === ContractStatus.Accepted && (
																<Alert severity="warning" style={{ marginTop: 8 }}>
																	{Language("CONTRACT_STATUS_WARNING")}
																</Alert>
															)}
														</React.Fragment>
													);
												}} />
										</FormField>
									</FormGroup>

									<FormGroup
										label={Language("TYPE")}
										labelPlacement="left-center"
										overflow="hidden"
										required
									>
										<Controller
											name="charterType"
											control={control}
											rules={{ required: true }}
											render={({ field }) => (
												<CharterTypeSelect
													{...field}
													onChange={option => {
														if (option) {
															field.onChange(option);
														}

														setValue("creditor", null);
														setValue("creditorContact", undefined);
														setValue("debtor", null);
														setValue("debtorContact", undefined);
													}}
													disabled={isCharterTypeFieldDisabled(field)}
													clearable={false}
												/>
											)}
										/>
									</FormGroup>

									<FormGroup
										label={Language("SUBTYPE")}
										labelPlacement="left-center"
										overflow="hidden"
										required
									>
										<Controller
											name="charterSubtype"
											control={control}
											rules={{ required: true }}
											render={({ field }) => (
												<CharterSubtypeSelect
													{...field}
													onChange={option => {
														if (option) {
															field.onChange(option);
														}
													}}
													customCharterSubtypes={Object.values(CharterSubtype)}
													disabled={isCharterTypeFieldDisabled(field)}
													disableClear={true}
												/>
											)}
										/>
									</FormGroup>

									<FormGroup
										label={Language("DESCRIPTION")}
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<FormField errors={errors} name="description">
											<Controller
												name="description"
												control={control}
												rules={{ required: true }}
												render={({ field }) => <Input disabled={isChildContract} {...field} />} />
										</FormField>
									</FormGroup>

									{hasOptionalFieldPermission(session, Fields.CONTRACT, "memo") && (
										<FormGroup
											label={Language("CONTRACT_MEMO")}
											labelPlacement="left-center"
											overflow="hidden"
										>
											<Controller
												name="memo"
												control={control}
												render={({ field }) => (
													<Input {...field} disabled={isChildContract} />
												)}
											/>
										</FormGroup>
									)}

									{hasOptionalFieldPermission(session, Fields.CONTRACT, "labels") && (
										<FormGroup
											label={Language("LABELS")}
											labelPlacement="left-center"
											overflow="hidden"
										>
											<Controller
												name="labels"
												control={control}
												render={({ field }) => (
													<LabelSelect
														{...field}
														onChange={options => field.onChange(options ?? [])}
														type={LabelType.Contract}
														disabled={field.disabled || isChildContract}
														multiple
													/>
												)}
											/>
										</FormGroup>
									)}

									<FormGroup
										label={Language("CONTRACT_CURRENCY")}
										labelPlacement="left-center"
										overflow="hidden">
										<Controller
											name="currency"
											control={control}
											render={({ field }) => <CurrencySelect
												disabled={isChildContract}
												{...field} />} />
									</FormGroup>

									<FormGroup
										label={Language("INVOICE_CREDITOR")}
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<FormField errors={errors} name="creditor">
											<Controller
												name="creditor"
												control={control}
												rules={{ required: true }}
												render={({ field }) => (
													<RelationSelect
														{...field}
														onChange={option => {
															field.onChange(option);
															setValue("creditorContact", undefined);
														}}
														disabled={isChildContract}
														types={getCreditorType()}
													/>
												)} />
										</FormField>
									</FormGroup>

									{creditor && (
										<FormGroup label={""} labelPlacement="left-center" overflow="hidden">
											<Controller
												name="creditorContact"
												control={control}
												render={({ field }) => (
													<Select
														{...field}
														disabled={isChildContract}
														placeholder={intl.formatMessage({ id: "SELECT_A_CONTACT_PERSON" })}
														fetchOptions={creditor?.contacts || []} />
												)} />
										</FormGroup>
									)}

									<FormGroup
										label={Language("INVOICE_DEBTOR")}
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<FormField errors={errors} name="debtor">
											<Controller
												name="debtor"
												control={control}
												rules={{ required: true }}
												render={({ field }) => (
													<RelationSelect
														{...field}
														disabled={isChildContract}
														onChange={option => {
															field.onChange(option);
															setValue("debtorContact", undefined);
														}}
														types={getDebtorType()} />
												)} />
										</FormField>
									</FormGroup>

									{debtor && (
										<FormGroup label={""} labelPlacement="left-center" overflow="hidden">
											<Controller
												name="debtorContact"
												control={control}
												render={({ field }) => (
													<Select
														{...field}
														disabled={isChildContract}
														placeholder={intl.formatMessage({ id: "SELECT_A_CONTACT_PERSON" })}
														fetchOptions={debtor?.contacts || []} />
												)} />
										</FormGroup>
									)}

									<FormGroup
										label={Language("BROKER")}
										labelPlacement="left-center"
										overflow="hidden">
										<FormField errors={errors} name="broker">
											<Controller
												name="broker"
												control={control}
												render={({ field }) => (
													<RelationSelect
														{...field}
														onChange={option => {
															field.onChange(option);
														}}
														types={[ RelationType.Broker ]} />
												)} />
										</FormField>
									</FormGroup>

									<FormGroup
										label={Language("VALID_BETWEEN")}
										labelPlacement="left-center"
										required={validityWindowRequired}
										overflow="hidden">
										<FormField
											errors={errors}
											name="validWindow"
											validation={intl.formatMessage({ id: "SELECT_VALID_UNTIL" })}>
											<Controller
												name="validWindow"
												control={control}
												rules={{
													required: validityWindowRequired,
													validate: (value) => {
														const isFilledIn = value?.[0] && value[1];
														if (!isFilledIn && validityWindowRequired) {
															return false;
														}
														if (charter && isFilledIn && charter.validFrom && charter.validUntil) {
															if (!(value[0]!.getTime() <= charter.validFrom && charter.validUntil <= value[1]!.getTime())) {
																setError("validWindow", {
																	type: "custom",
																	message: intl.formatMessage({ id: "CHARTER_LINKED_VOYAGES_OUT_OF_WINDOW" }),
																});
																return false;
															}
														}
														return true;
													},
												}}
												render={({ field }) => (
													<DateRangePicker
														startDate={field.value?.[0] || undefined}
														endDate={field.value?.[1] || undefined}
														excludeDateIntervals={charterType === CharterType.Purchase && charterSubtype === CharterSubtype.Spot ? [ {
															start: moment(Math.min(...(charters?.flatMap(charter => charter.validFrom ? [ charter.validFrom ] : []) ?? []))).toDate(),
															end: moment(Math.max(...(charters?.flatMap(charter => charter.validUntil ? [ charter.validUntil ] : []) ?? []))).toDate(),
														} ] : undefined}
														disabled={field.disabled || isChildContract}
														onChange={window => field.onChange(window)}
														showYearDropdown
													/>
												)}
											/>
										</FormField>
									</FormGroup>

									{hasOptionalFieldPermission(session, Fields.CONTRACT, "noticePeriod") && (
										<FormGroup
											label={Language("CONTRACT_NOTICE_PERIOD")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="noticePeriod"
												control={control}
												render={({ field }) => (
													<NoticePeriodSelect
														disabled={isChildContract}
														{...field}
													/>
												)}
											/>
										</FormGroup>
									)}

									{id && hasOptionalFieldPermission(session, Fields.CONTRACT, "versionNumber") && (
										<FormGroup
											label={Language("VERSION_NUMBER")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="versionNumber"
												control={control}
												render={({ field }) => (
													<Input disabled={isChildContract} {...field} />
												)}
											/>
										</FormGroup>
									)}

									{isTimePurchaseContract && hasOptionalFieldPermission(session, Fields.CONTRACT, "allIn") && (
										<FormGroup
											label={Language("ALL_IN")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="allIn"
												control={control}
												render={({ field }) => (
													<Checkbox
														disabled={isChildContract}
														{...field}
														value={undefined}
														checked={field.value}
													/>
												)}
											/>
										</FormGroup>
									)}

									{isTimePurchaseContract && hasOptionalFieldPermission(session, Fields.CONTRACT, "regions") && (
										<FormGroup
											label={Language("REGIONS")}
											labelPlacement="left-center"
											overflow="hidden"
										>
											<Controller
												name="regions"
												control={control}
												render={({ field }) => (
													<LocationSelect
														{...field}
														onChange={(option) => field.onChange(option || [])}
														types={[ LocationType.Area, LocationType.Port, LocationType.WaitingArea ]}
														multiple
													/>
												)}
											/>
										</FormGroup>
									)}

									{isTimePurchaseContract && hasOptionalFieldPermission(session, Fields.CONTRACT, "productCategory") && (
										<FormGroup
											label={Language("PRODUCT_CATEGORY")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="productCategory"
												control={control}
												render={({ field }) => <ProductCategorySelect {...field} />} />
										</FormGroup>
									)}

									{parentContractCompatibleSubtypes.includes(charterSubtype) &&
										hasOptionalFieldPermission(session, Fields.CONTRACT, "parentContract") && (
										<FormGroup
											label={Language("IS_PARENT_CONTRACT")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="isParentContract"
												control={control}
												render={({ field }) => (
													<Checkbox
														disabled={id != null}
														onChange={field.onChange}
														checked={field.value}
													/>
												)}
											/>
										</FormGroup>
									)}

									{canBeBackToBack && (
										<FormGroup
											label={Language("BACK_TO_BACK")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="isBackToBack"
												control={control}
												render={({ field }) => <Checkbox onChange={field.onChange} checked={field.value ?? undefined} />} />
										</FormGroup>
									)}

									{charterType === CharterType.Sales && (
										<FormGroup
											label={Language("INCLUDE_PRICING_IN_NOMINATION")}
											labelPlacement="left-center"
											overflow="hidden">
											<Controller
												name="includePricingInNomination"
												control={control}
												render={({ field }) => <Checkbox onChange={field.onChange} checked={field.value ?? undefined} />} />
										</FormGroup>
									)}

									<FormGroup
										label={Language("TERMS_AND_CONDITIONS")}
										labelPlacement="left-center"
										overflow="hidden">
										<Controller
											name="termsAndConditions"
											control={control}
											render={({ field }) => <TermsAndConditionsSelect {...field} />} />
									</FormGroup>
								</Card>
							</Grid>
						)}

						<Grid size={{ md: hideContractDetails ? 12 : 8, xs: 12 }}>
							{hasOptionalFieldPermission(session, Fields.CONTRACT, "contractOptions") && id ? (
								<Card disablePadding>
									<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
										<Tabs
											variant='scrollable'
											value={selectedTab}
											onChange={(_event, newValue) => selectTab(newValue)}>
											<Tab label={Language("AGREEMENTS")} value={TABS.AGREEMENTS} />

											{isTimePurchaseContract && (
												<Tab label={Language("CONTRACT_OPTIONS")} value={TABS.OPTIONS} />
											)}
										</Tabs>
									</Box>

									{selectedTab === TABS.AGREEMENTS && (
										<AgreementCreateEdit
											charter={linkedCharter}
											currency={currency ?? defaultCurrency}
											session={session} />

									)}

									{selectedTab === TABS.OPTIONS && (
										<ContractOptionsForm
											control={control}
											contractValidWindow={validWindow}
											contractOptions={contractOptions}
											agreements={agreements}
											takenOptionContract={takenOptionContract}
											currency={currency ?? defaultCurrency}
											setValue={setValue}
										/>
									)}
								</Card>
							) : (
								<AgreementCreateEdit
									charter={linkedCharter}
									currency={currency ?? defaultCurrency}
									session={session} />
							)}
						</Grid>
					</Grid>
				</TrackedEntityForm>
			</FormProvider>
		</div>
	);
};

export default ContractCreateEdit;
